import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Read from sessionStorage first (unchecked-remember session), then localStorage
  // (checked-remember or legacy pre-round-D session). Legacy localStorage sessions
  // keep working without a forced logout.
  const [token, setToken] = useState(
    sessionStorage.getItem("token") || localStorage.getItem("token")
  );
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState(() => {
    // F19: guard the boot-time JSON.parse — a corrupt value must not white-screen the app.
    try {
      const raw = localStorage.getItem("selectedUnit");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      try { localStorage.removeItem("selectedUnit"); } catch (_) {}
      return null;
    }
  });

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await api.get("/auth/me");
          setUser(response.data);
        } catch (error) {
          console.error("Auth init error:", error);
          sessionStorage.removeItem("token");
          localStorage.removeItem("token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password, remember = true) => {
    const response = await api.post("/auth/login", { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    const store = remember ? localStorage : sessionStorage;
    const other = remember ? sessionStorage : localStorage;
    // Write to chosen storage, and wipe the other so read-order can't return stale.
    store.setItem("token", access_token);
    if (refresh_token) store.setItem("refresh_token", refresh_token);
    other.removeItem("token");
    other.removeItem("refresh_token");
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await api.post("/auth/register", data);
    return response.data;
  };

  const logout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refresh_token");
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("selectedUnit");
    setToken(null);
    setUser(null);
    setSelectedUnit(null);
  };

  const selectUnit = (unit) => {
    setSelectedUnit(unit);
    localStorage.setItem("selectedUnit", JSON.stringify(unit));
  };

  const hasPermission = useCallback((module, action = "view") => {
    if (!user) return false;
    // Super-roles: admin & management have full access to every module/action
    // (mirrors the route guards and sidebar, which already bypass them). Without
    // this, in-page controls gated by hasPermission (e.g. canEdit) were hidden
    // for admin even though the backend now allows the action.
    if (user.role === "admin" || user.role === "management") return true;
    if (!user.permissions) return false;
    const modulePerms = user.permissions[module];
    if (!modulePerms) return false;
    return modulePerms[action] === true;
  }, [user]);

  const refreshPermissions = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      console.error("Failed to refresh permissions:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        selectedUnit,
        login,
        register,
        logout,
        selectUnit,
        hasPermission,
        refreshPermissions,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
